import { useMemo } from 'react';
import {
  useQuantzData,
  QuantzOperation,
  QuantzQueryCacheDuration,
  QuantzEventType,
  QuantzProperty,
} from '@hotjar/quantz';
import type { QuantzCategoryDataItem } from '@hotjar/quantz';
import { URLConverter, QuerySubKeyEnum } from '@hotjar/global-filters';
import type { GlobalFilter } from '@hotjar/global-filters';

import {
  mapDeviceToGlobalFilter,
  mapTargetingValueToRadioTextFilterOperation,
} from '../lib/mapHeatmapParamsToGlobalFilters';
import type { AggregatePageInsightsHookParams } from '../constants-types/AggregatePageInsights';

export function useGetPageDropOffRate({
  selectedTargetingValue,
  selectedDateRange,
  selectedDeviceType,
  searchPattern,
  siteId,
  enabled,
}: AggregatePageInsightsHookParams): number | undefined {
  const tempSearchOperand = URLConverter.convertUiOperation(
    mapTargetingValueToRadioTextFilterOperation(selectedTargetingValue),
  )?.operation;
  const searchOperand = tempSearchOperand ? tempSearchOperand.toString() : 'SIMPLE_MATCH';

  const sessionFilters: GlobalFilter = useMemo(
    () => ({
      dateRange: selectedDateRange,
      filterList: [mapDeviceToGlobalFilter(selectedDeviceType)],
    }),
    [selectedDateRange, selectedDeviceType],
  );

  const { data } = useQuantzData(
    {
      sessionFilters,
      siteId,
      operation: QuantzOperation.COUNT,
      groupBy: QuantzProperty.DROP_OFF,
      eventType: QuantzEventType.PAGE_VISIT,
      eventFilters: {
        [searchOperand]: { [QuerySubKeyEnum.page_path]: searchPattern },
      },
    },
    enabled,
    QuantzQueryCacheDuration.Mid,
  );

  if (
    data?.data?.resultSeries != null &&
    data?.data?.total != null &&
    data.data.total > 0 &&
    data.data.resultSeries[0].values.length > 0
  ) {
    const values: QuantzCategoryDataItem[] = data.data.resultSeries[0]
      .values as QuantzCategoryDataItem[];

    const totalDropOffs = values.find((val) => val.category === '1') || { value: 0 };
    const dropOffRate = totalDropOffs.value / data.data.total;

    return Math.round(dropOffRate * 1000) / 10;
  }

  return undefined;
}
